import React, { useState, useEffect } from 'react';
import { 
  createRingPack,
  getRingPacks,
  toggleRingPackAvailability,
  deleteRingPack,
  getCustomPurchaseableRings,
  ALL_RINGS,
  RING_CATEGORIES,
  RING_SETS,
  RING_SET_INFO,
  RING_RARITIES
} from '../services/profileRingService';
import ProfileRing from './ProfileRing';

function AdminRingPackManager({ isOpen, onClose, adminUserId }) {
  const [ringPacks, setRingPacks] = useState([]);
  const [availableRings, setAvailableRings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('manage'); // 'manage' or 'create'
  
  // New pack creation form
  const [newPack, setNewPack] = useState({
    name: '',
    description: '',
    theme: '',
    price: '',
    discountPercent: '10',
    ringIds: [],
    isLimitedTime: false,
    availableUntil: '',
    maxPurchases: '',
    tags: []
  });
  const [creating, setCreating] = useState(false);
  const [selectedRings, setSelectedRings] = useState(new Set());
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setLoading(true);
      try {
        // Load existing ring packs
        const packs = await getRingPacks();
        setRingPacks(packs);
        
        // Load available rings (both default and custom)
        const customRings = await getCustomPurchaseableRings();
        const defaultRings = Object.entries(ALL_RINGS)
          .filter(([_, ring]) => ring.category === RING_CATEGORIES.PURCHASEABLE)
          .map(([id, ring]) => ({ id, ...ring }));
        
        setAvailableRings([...defaultRings, ...customRings]);
      } catch (error) {
        console.error('Error loading ring pack data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen]);

  const handleCreatePack = async (e) => {
    e.preventDefault();
    
    if (!newPack.name.trim() || !newPack.description.trim() || selectedRings.size === 0) {
      alert('Please fill in name, description, and select at least one ring');
      return;
    }
    
    if (selectedRings.size < 2) {
      alert('A pack must contain at least 2 rings');
      return;
    }
    
    setCreating(true);
    try {
      const selectedRingIds = Array.from(selectedRings);
      const selectedRingData = availableRings.filter(ring => selectedRingIds.includes(ring.id));
      
      // Calculate total individual price
      const totalIndividualPrice = selectedRingData.reduce((sum, ring) => sum + (ring.price || 0), 0);
      
      // Calculate pack price with discount
      const discountAmount = (totalIndividualPrice * (parseInt(newPack.discountPercent) / 100));
      const packPrice = parseInt(newPack.price) || Math.max(1, totalIndividualPrice - discountAmount);
      
      const packData = {
        name: newPack.name.trim(),
        description: newPack.description.trim(),
        theme: newPack.theme.trim(),
        price: packPrice,
        originalPrice: totalIndividualPrice,
        discountPercent: parseInt(newPack.discountPercent),
        ringIds: selectedRingIds,
        ringCount: selectedRingIds.length,
        isLimitedTime: newPack.isLimitedTime,
        availableUntil: newPack.availableUntil ? new Date(newPack.availableUntil).getTime() : null,
        maxPurchases: newPack.maxPurchases ? parseInt(newPack.maxPurchases) : null,
        purchaseCount: 0,
        tags: newPack.tags,
        createdBy: adminUserId,
        createdAt: Date.now(),
        isActive: true
      };
      
      const packId = await createRingPack(packData);
      
      if (packId) {
        // Add to local state
        setRingPacks(prev => [...prev, { id: packId, ...packData }]);
        
        // Reset form
        setNewPack({
          name: '',
          description: '',
          theme: '',
          price: '',
          discountPercent: '10',
          ringIds: [],
          isLimitedTime: false,
          availableUntil: '',
          maxPurchases: '',
          tags: []
        });
        setSelectedRings(new Set());
        
        alert('Ring pack created successfully!');
        setActiveTab('manage');
      } else {
        alert('Failed to create ring pack');
      }
    } catch (error) {
      console.error('Error creating ring pack:', error);
      alert('Failed to create ring pack');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleRingSelection = (ringId) => {
    const newSelected = new Set(selectedRings);
    if (newSelected.has(ringId)) {
      newSelected.delete(ringId);
    } else {
      newSelected.add(ringId);
    }
    setSelectedRings(newSelected);
  };

  const handleTogglePackAvailability = async (packId, currentStatus) => {
    try {
      const success = await toggleRingPackAvailability(packId, !currentStatus);
      if (success) {
        setRingPacks(prev => prev.map(pack => 
          pack.id === packId 
            ? { ...pack, isActive: !currentStatus }
            : pack
        ));
      }
    } catch (error) {
      console.error('Error toggling pack availability:', error);
      alert('Failed to update pack availability');
    }
  };

  const handleDeletePack = async (packId) => {
    if (!window.confirm('Are you sure you want to delete this ring pack? This cannot be undone.')) {
      return;
    }
    
    try {
      const success = await deleteRingPack(packId);
      if (success) {
        setRingPacks(prev => prev.filter(pack => pack.id !== packId));
        alert('Ring pack deleted successfully');
      } else {
        alert('Failed to delete ring pack');
      }
    } catch (error) {
      console.error('Error deleting ring pack:', error);
      alert('Failed to delete ring pack');
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !newPack.tags.includes(newTag.trim())) {
      setNewPack({
        ...newPack,
        tags: [...newPack.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setNewPack({
      ...newPack,
      tags: newPack.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const calculatePackSavings = (originalPrice, packPrice) => {
    return originalPrice - packPrice;
  };

  const isPackExpired = (pack) => {
    return pack.isLimitedTime && pack.availableUntil && Date.now() > pack.availableUntil;
  };

  const isPackSoldOut = (pack) => {
    return pack.maxPurchases && pack.purchaseCount >= pack.maxPurchases;
  };

  if (!isOpen) return null;

  return (
    <div className="admin-ring-pack-modal-overlay" onClick={onClose}>
      <div className="admin-ring-pack-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="admin-ring-pack-modal-header">
          <h2>Ring Pack Manager</h2>
          <button className="admin-ring-pack-modal-close" onClick={onClose}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="admin-ring-tabs">
          <button 
            className={`tab-button ${activeTab === 'manage' ? 'active' : ''}`}
            onClick={() => setActiveTab('manage')}
          >
            Manage Packs
          </button>
          <button 
            className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            Create Pack
          </button>
        </div>

        {loading ? (
          <div className="admin-ring-loading">
            <div className="emoji-loader">
              <span className="emoji-item">📦</span>
              <span className="emoji-item">💍</span>
              <span className="emoji-item">⚡</span>
            </div>
            <p>Loading ring packs...</p>
          </div>
        ) : (
          <>
            {/* Manage Packs Tab */}
            {activeTab === 'manage' && (
              <div className="manage-ring-packs-section">
                <h3>Ring Pack Collection</h3>
                
                {ringPacks.length > 0 ? (
                  <div className="ring-packs-grid">
                    {ringPacks.map(pack => (
                      <div key={pack.id} className={`ring-pack-item ${!pack.isActive ? 'inactive' : ''}`}>
                        <div className="pack-header">
                          <div className="pack-title">{pack.name}</div>
                          <div className="pack-theme">{pack.theme}</div>
                        </div>
                        
                        <div className="pack-description">{pack.description}</div>
                        
                        {/* Ring Preview */}
                        <div className="pack-rings-preview">
                          {pack.ringIds.slice(0, 3).map(ringId => {
                            const ring = availableRings.find(r => r.id === ringId);
                            return ring ? (
                              <div key={ringId} className="pack-ring-mini">
                                <ProfileRing ringId={ringId} size="small">
                                  <div className="mini-avatar">
                                    <span>💍</span>
                                  </div>
                                </ProfileRing>
                              </div>
                            ) : null;
                          })}
                          {pack.ringIds.length > 3 && (
                            <div className="pack-ring-count">+{pack.ringIds.length - 3}</div>
                          )}
                        </div>
                        
                        <div className="pack-pricing">
                          <div className="pack-price">🪙 {pack.price}</div>
                          <div className="pack-original-price">
                            <span className="strikethrough">🪙 {pack.originalPrice}</span>
                          </div>
                          <div className="pack-savings">
                            Save 🪙 {calculatePackSavings(pack.originalPrice, pack.price)} 
                            ({pack.discountPercent}%)
                          </div>
                        </div>
                        
                        <div className="pack-meta">
                          <span className="ring-count">{pack.ringCount} rings</span>
                          <span className="purchase-count">{pack.purchaseCount} sold</span>
                          {pack.isLimitedTime && (
                            <span className={`limited-time ${isPackExpired(pack) ? 'expired' : ''}`}>
                              ⏰ {isPackExpired(pack) ? 'Expired' : 'Limited Time'}
                            </span>
                          )}
                          {isPackSoldOut(pack) && (
                            <span className="sold-out">🚫 Sold Out</span>
                          )}
                        </div>
                        
                        {pack.tags.length > 0 && (
                          <div className="pack-tags">
                            {pack.tags.map(tag => (
                              <span key={tag} className="pack-tag">{tag}</span>
                            ))}
                          </div>
                        )}
                        
                        <div className="pack-actions">
                          <button 
                            className={`toggle-pack-button ${pack.isActive ? 'active' : 'inactive'}`}
                            onClick={() => handleTogglePackAvailability(pack.id, pack.isActive)}
                          >
                            {pack.isActive ? 'Disable' : 'Enable'}
                          </button>
                          <button 
                            className="delete-pack-button"
                            onClick={() => handleDeletePack(pack.id)}
                          >
                            Delete
                          </button>
                        </div>
                        
                        {!pack.isActive && (
                          <div className="pack-inactive-overlay">
                            <span>Disabled</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-ring-packs">
                    <div className="no-packs-icon">📦</div>
                    <div className="no-packs-text">
                      No ring packs created yet.
                      <br />
                      Create your first pack to get started!
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Create Pack Tab */}
            {activeTab === 'create' && (
              <div className="create-ring-pack-section">
                <h3>Create New Ring Pack</h3>
                
                <div className="create-pack-layout">
                  <div className="pack-form-container">
                    <form onSubmit={handleCreatePack} className="create-pack-form">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Pack Name</label>
                          <input
                            type="text"
                            value={newPack.name}
                            onChange={(e) => setNewPack({...newPack, name: e.target.value})}
                            placeholder="Enter pack name"
                            maxLength={50}
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>Theme</label>
                          <input
                            type="text"
                            value={newPack.theme}
                            onChange={(e) => setNewPack({...newPack, theme: e.target.value})}
                            placeholder="e.g., Galaxy, Nature, Neon"
                            maxLength={30}
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={newPack.description}
                          onChange={(e) => setNewPack({...newPack, description: e.target.value})}
                          placeholder="Describe this ring pack"
                          maxLength={200}
                          rows={3}
                          required
                        />
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label>Discount Percentage</label>
                          <input
                            type="number"
                            value={newPack.discountPercent}
                            onChange={(e) => setNewPack({...newPack, discountPercent: e.target.value})}
                            min="5"
                            max="50"
                            placeholder="10"
                          />
                          <div className="form-help">5-50% discount from individual prices</div>
                        </div>
                        
                        <div className="form-group">
                          <label>Custom Price (Optional)</label>
                          <input
                            type="number"
                            value={newPack.price}
                            onChange={(e) => setNewPack({...newPack, price: e.target.value})}
                            min="1"
                            max="10000"
                            placeholder="Auto-calculated"
                          />
                          <div className="form-help">Leave empty for auto-calculation</div>
                        </div>
                      </div>
                      
                      {/* Limited Time Options */}
                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={newPack.isLimitedTime}
                            onChange={(e) => setNewPack({...newPack, isLimitedTime: e.target.checked})}
                          />
                          Limited Time Offer
                        </label>
                      </div>
                      
                      {newPack.isLimitedTime && (
                        <div className="form-row">
                          <div className="form-group">
                            <label>Available Until</label>
                            <input
                              type="datetime-local"
                              value={newPack.availableUntil}
                              onChange={(e) => setNewPack({...newPack, availableUntil: e.target.value})}
                            />
                          </div>
                          
                          <div className="form-group">
                            <label>Max Purchases</label>
                            <input
                              type="number"
                              value={newPack.maxPurchases}
                              onChange={(e) => setNewPack({...newPack, maxPurchases: e.target.value})}
                              min="1"
                              placeholder="Unlimited"
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Tags */}
                      <div className="form-group">
                        <label>Tags</label>
                        <div className="tag-input-container">
                          <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            placeholder="Add tag"
                            maxLength={20}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                          />
                          <button type="button" onClick={handleAddTag}>Add</button>
                        </div>
                        {newPack.tags.length > 0 && (
                          <div className="current-tags">
                            {newPack.tags.map(tag => (
                              <span key={tag} className="tag-chip">
                                {tag}
                                <button type="button" onClick={() => handleRemoveTag(tag)}>×</button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <button 
                        type="submit" 
                        className="create-pack-button"
                        disabled={creating || selectedRings.size < 2}
                      >
                        {creating ? 'Creating...' : `Create Pack (${selectedRings.size} rings)`}
                      </button>
                    </form>
                  </div>
                  
                  {/* Ring Selection */}
                  <div className="ring-selection-container">
                    <h4>Select Rings ({selectedRings.size} selected)</h4>
                    <div className="ring-selection-grid">
                      {availableRings.map(ring => (
                        <div 
                          key={ring.id} 
                          className={`selectable-ring ${selectedRings.has(ring.id) ? 'selected' : ''}`}
                          onClick={() => handleToggleRingSelection(ring.id)}
                        >
                          <ProfileRing ringId={ring.id} size="small">
                            <div className="mini-avatar">
                              <span>💍</span>
                            </div>
                          </ProfileRing>
                          <div className="ring-info">
                            <div className="ring-name">{ring.name}</div>
                            <div className="ring-price">🪙 {ring.price || 0}</div>
                          </div>
                          {selectedRings.has(ring.id) && (
                            <div className="selection-indicator">✓</div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {selectedRings.size > 0 && (
                      <div className="selection-summary">
                        <div>Selected: {selectedRings.size} rings</div>
                        <div>Total Value: 🪙 {
                          availableRings
                            .filter(ring => selectedRings.has(ring.id))
                            .reduce((sum, ring) => sum + (ring.price || 0), 0)
                        }</div>
                        <div>Pack Price: 🪙 {
                          newPack.price || Math.max(1, 
                            availableRings
                              .filter(ring => selectedRings.has(ring.id))
                              .reduce((sum, ring) => sum + (ring.price || 0), 0) 
                            * (1 - parseInt(newPack.discountPercent || 10) / 100)
                          )
                        }</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminRingPackManager;